import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface QueueItem {
  domain: string;
  consent_status: string;
  priority: string;
  next_crawl: string | null;
  last_checked: string | null;
}

interface QueueData {
  queue: QueueItem[];
  upcoming: QueueItem[];
}

function CrawlQueue() {
  const [data, setData] = useState<QueueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchQueue();
  }, []);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/crawl-queue`);
      if (!response.ok) throw new Error('Failed to fetch crawl queue');
      
      const queueData = await response.json();
      setData(queueData);
    } catch (err) {
      setError('Failed to load crawl queue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const triggerCrawl = async (domain: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/domains/${domain}/crawl`, {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to trigger crawl');

      alert(`Crawl queued for ${domain}`);
      fetchQueue();
    } catch (err) {
      alert('Failed to trigger crawl');
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading crawl queue...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchQueue}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Crawl Queue</h1>
        <p>Monitor pending and upcoming crawl jobs</p>
      </div>

      {/* Ready to Crawl */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Ready to Crawl ({data?.queue.length || 0})</h2>
          <button className="btn btn-secondary btn-sm" onClick={fetchQueue}>
            Refresh
          </button>
        </div>
        
        {data?.queue && data.queue.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Last Checked</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.queue.map((item) => (
                <tr key={item.domain}>
                  <td>
                    <Link to={`/admin/domains/${item.domain}`}>
                      {item.domain}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge badge-${item.consent_status === 'allowed' ? 'success' : item.consent_status === 'denied' ? 'error' : 'warning'}`}>
                      {item.consent_status}
                    </span>
                  </td>
                  <td>
                    <span className={`badge badge-${item.priority === 'high' ? 'error' : item.priority === 'low' ? 'warning' : 'success'}`}>
                      {item.priority}
                    </span>
                  </td>
                  <td>
                    {item.last_checked
                      ? new Date(item.last_checked).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => triggerCrawl(item.domain)}
                    >
                      Force Crawl
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card empty-state">
            <p>No domains ready for crawling</p>
            <p className="text-secondary mt-2">
              All scheduled crawls are up to date.
            </p>
          </div>
        )}
      </div>

      {/* Upcoming Crawls */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Upcoming Crawls ({data?.upcoming.length || 0})</h2>
        </div>
        
        {data?.upcoming && data.upcoming.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Scheduled For</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming.map((item) => (
                <tr key={item.domain}>
                  <td>
                    <Link to={`/admin/domains/${item.domain}`}>
                      {item.domain}
                    </Link>
                  </td>
                  <td>
                    <span className={`badge badge-${item.consent_status === 'allowed' ? 'success' : item.consent_status === 'denied' ? 'error' : 'warning'}`}>
                      {item.consent_status}
                    </span>
                  </td>
                  <td>{item.priority}</td>
                  <td>
                    {item.next_crawl
                      ? new Date(item.next_crawl).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => triggerCrawl(item.domain)}
                    >
                      Crawl Now
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card empty-state">
            <p>No upcoming crawls scheduled</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CrawlQueue;
